import { hasLowerLevel, mapInfo, type Level, type MapId } from "@69ly/maps";
import { EXECUTION_KINDS, planClock, planPoints, type Execution, type ExecutionKind } from "@69ly/shared";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { PlanControls } from "../components/plan/PlanControls.tsx";
import { PlanParts, type PartsContext } from "../components/plan/PlanParts.tsx";
import { PlanStage } from "../components/plan/PlanStage.tsx";
import { PlanTimeline } from "../components/plan/PlanTimeline.tsx";
import { focusOn } from "../components/Radar.tsx";
import { Select } from "../components/Select.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { eventTimes, floorOf, isPlayer, usePlanSchedule, usePlayhead, type PlanActor } from "../lib/plan.ts";
import { useMe } from "../lib/queries.ts";
import { useDeleteExecution, useExecutions, usePlantSpots } from "../lib/teamplay-queries.ts";
import { MapBar, useMapParam } from "./MapView.tsx";

/** The page fills the window from where it starts: map, rail and timeline all in view. */
export function useFillHeight(ref: RefObject<HTMLElement | null>, enabled = true) {
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!enabled) return;
    const fit = () => {
      const el = ref.current;
      if (!el) return;
      // Phones and narrow windows: the page scrolls as usual.
      if (window.innerWidth < 960) return setHeight(null);
      const top = el.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(560, window.innerHeight - top - 12));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [ref, enabled]);
  return height;
}

/** How a player of the plan is named on the map and the timeline. */
export function useActorNames(e: Pick<Execution, "members">) {
  const { t } = useTranslation();
  return {
    short: (a: PlanActor) => (isPlayer(a) ? (e.members.find((m) => m.id === a.memberId)?.name ?? t(`role.${a.role}`)) : a.label || t("plan.opponent")),
    title: (a: PlanActor) =>
      isPlayer(a)
        ? [`${a.slot} · ${t(`role.${a.role}`)}`, e.members.find((m) => m.id === a.memberId)?.name, t(`plan.weapon.${a.weapon}`)].filter(Boolean).join(" · ")
        : [t("plan.opponent"), a.label].filter(Boolean).join(" · "),
  };
}

function PlanViewer({ map, execution, onDeleted, startFocus }: { map: MapId; execution: Execution; onDeleted: () => void; startFocus: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe().data?.user?.id ?? null;
  const del = useDeleteExecution();
  const plants = usePlantSpots(execution.teamId ?? "personal", map);
  const { plan } = execution;
  const { schedule, lookup } = usePlanSchedule(plan, execution.stuffs, execution.combos);
  const length = Math.max(schedule.span, Math.ceil(schedule.duration + 1));
  const playhead = usePlayhead(length);
  // "me" opens on my part (from the live page), a player's id on theirs.
  const [focus, setFocus] = useState<string | null>(() => {
    if (startFocus === "me") return plan.players.find((p) => p.memberId && p.memberId === me)?.id ?? null;
    return [...plan.players, ...plan.opponents].some((a) => a.id === startFocus) ? startFocus : null;
  });
  // Who I am may come after the first paint.
  const meApplied = useRef(false);
  useEffect(() => {
    if (startFocus !== "me" || !me || meApplied.current) return;
    meApplied.current = true;
    const mine = plan.players.find((p) => p.memberId === me);
    if (mine) setFocus(mine.id);
  }, [startFocus, me, plan.players]);
  const names = useActorNames(execution);
  const firstFloor: Level = floorOf(map, plan.players[0]?.start.z ?? plan.bomb?.z ?? null);
  const [level, setLevel] = useState<Level>(firstFloor);
  const events = useMemo(() => eventTimes(schedule, plan.chapters), [schedule, plan.chapters]);
  // Zoomed on what the plan uses, once: playing must not move the view.
  const view = useMemo(() => focusOn(map, planPoints(plan, lookup), { maxScale: 2.6, pad: 0.1 }), [map, plan, lookup]);
  const page = useRef<HTMLDivElement>(null);
  const height = useFillHeight(page);
  const ctx: PartsContext = useMemo(() => ({ map, plan, schedule, lookup, stuffs: execution.stuffs, combos: execution.combos, members: execution.members }), [map, plan, schedule, lookup, execution]);
  const now = Math.floor(playhead.elapsed * 4) / 4;
  const afterPlant = execution.kind === "execute" ? (plants.data ?? []).filter((p) => p.kind === "postplant" && p.site === execution.target) : [];

  return (
    <div className="plan-page" ref={page} style={height ? { height } : undefined}>
      <div className="plan-main">
        <div className="plan-map-wrap">
          <div className="plan-map map-frame">
            <PlanStage
              map={map}
              mapName={mapInfo(map).name}
              level={level}
              plan={plan}
              schedule={schedule}
              elapsed={playhead.elapsed}
              focus={focus}
              selected={null}
              view={view}
              actorTitle={names.title}
              pickZ={null}
            />
            {hasLowerLevel(map) && (
              <div className="map-overlay map-overlay-bottom glass">
                <div className="segmented" role="group" aria-label={t("level.label")}>
                  {(["upper", "lower"] as const).map((l) => (
                    <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                      {t(`level.${l}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <aside className="plan-rail card">
          <div className="plan-rail-head">
            <div>
              <p className="eyebrow">
                {t(`plan.kind.${execution.kind}`)} · {t(`area.${execution.target}`)} · {planClock(plan.window.from)} → {planClock(plan.window.to)}
              </p>
              <h2 className="panel-title">{execution.title}</h2>
              <p className="muted small">{t("mapView.by", { name: execution.author.name })}</p>
            </div>
            {execution.canEdit && (
              <div className="row">
                <Link to={`/executions/${execution.id}/edit`} className="btn btn-secondary btn-small">
                  {t("mapView.edit")}
                </Link>
                <button
                  type="button"
                  className="btn btn-quiet btn-small btn-danger"
                  disabled={del.isPending}
                  onClick={() => {
                    if (window.confirm(t("execution.confirmDelete", { title: execution.title }))) del.mutate(execution.id, { onSuccess: onDeleted });
                  }}
                >
                  {t("detail.delete")}
                </button>
              </div>
            )}
          </div>
          {execution.kind === "retake" && plan.bombKnownAt !== null && <p className="small plan-bomb-note">{t("plan.bombKnownAt", { time: planClock(plan.bombKnownAt) })}</p>}
          <PlanParts ctx={ctx} now={now} focus={focus} onFocus={setFocus} meId={me} onSeek={playhead.seek} />
          {execution.notes && <p className="notes small">{execution.notes}</p>}
          {afterPlant.length > 0 && (
            <section>
              <h3 className="eyebrow">{t("execution.afterPlant")}</h3>
              <ul className="plant-links small">
                {afterPlant.map((p) => (
                  <li key={p.id}>
                    <Link to={`/library/${map}/post-plant?spot=${p.id}`}>
                      <span className="c4-tag">C4</span> {p.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <p className="muted small">
            <Link to={`/library/${map}/learn`}>{t("execution.learnLink")}</Link>
            {" · "}
            <button type="button" className="link-button" onClick={() => navigate(`/library/${map}/executions/new?kind=${execution.kind}&copy=${execution.id}`)}>
              {t("plan.duplicate")}
            </button>
          </p>
        </aside>
      </div>
      <div className="plan-bottom card">
        <PlanControls plan={plan} schedule={schedule} length={length} events={events} playhead={playhead} />
        <PlanTimeline plan={plan} schedule={schedule} length={length} elapsed={playhead.elapsed} onSeek={playhead.seek} focus={focus} actorName={names.short} />
      </div>
    </div>
  );
}

/**
 * Executions, setups and retakes of a map: pick one in the bar, watch it play on
 * a big map with each player's part beside it and the timeline under it.
 */
export function ExecutionsPage() {
  const { t } = useTranslation();
  const map = useMapParam();
  const { scope } = useCurrentTeam();
  const executions = useExecutions(scope, map ?? "");
  const [params, setParams] = useSearchParams();
  const kind = (EXECUTION_KINDS as readonly string[]).includes(params.get("k") ?? "") ? (params.get("k") as ExecutionKind) : null;

  if (!map) return <Navigate to="/library" replace />;
  if (executions.isPending) return <Loading />;
  if (executions.error) return <ErrorState message={errorText(t, executions.error)} onRetry={() => void executions.refetch()} />;

  const all = executions.data;
  const shown = all.filter((e) => !kind || e.kind === kind);
  const current = shown.find((e) => e.id === params.get("e")) ?? shown[0] ?? null;
  const set = (change: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(change)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  const newLinks = (
    <div className="row plan-new">
      {EXECUTION_KINDS.map((k) => (
        <Link key={k} to={`/library/${map}/executions/new?kind=${k}`} className={`btn ${k === (kind ?? "execute") ? "btn-primary" : "btn-secondary"} btn-small`}>
          <Icon name="plus" size={14} />
          {t(`plan.new.${k}`)}
        </Link>
      ))}
    </div>
  );

  return (
    <div className="library">
      <MapBar map={map} section="executions" count={all.length} countLabel={t("execution.count", { count: all.length })} actions={newLinks} />
      <div className="plan-bar">
        <div className="segmented segmented-small" role="group" aria-label={t("plan.kindFilter")}>
          <button type="button" className="seg" aria-pressed={kind === null} onClick={() => set({ k: null, e: null })}>
            {t("plan.all")}
          </button>
          {EXECUTION_KINDS.map((k) => (
            <button key={k} type="button" className="seg" aria-pressed={kind === k} onClick={() => set({ k, e: null })}>
              {t(`plan.kinds.${k}`)} <span className="muted">{all.filter((e) => e.kind === k).length}</span>
            </button>
          ))}
        </div>
        {shown.length > 0 && (
          <Select
            className="plan-pick"
            label={t("plan.pick")}
            value={current?.id ?? null}
            placeholder={t("plan.pick")}
            groups={EXECUTION_KINDS.filter((k) => shown.some((e) => e.kind === k)).map((k) => ({
              label: t(`plan.kinds.${k}`),
              options: shown
                .filter((e) => e.kind === k)
                .map((e) => ({
                  value: e.id,
                  label: e.title,
                  hint: `${t(`areaShort.${e.target}`)} · ${t("plan.playerCount", { count: e.plan.players.length })}`,
                  icon: <span className={`plan-pick-site site-${e.target}`}>{t(`areaShort.${e.target}`)}</span>,
                })),
            }))}
            onChange={(v) => set({ e: v, p: null })}
          />
        )}
      </div>
      {current ? (
        <PlanViewer key={current.id} map={map} execution={current} startFocus={params.get("p")} onDeleted={() => set({ e: null, p: null })} />
      ) : (
        <div className="page">
          <div className="card learn-empty">
            <h1 className="section-title">{kind ? t(`plan.empty.${kind}`, { map: mapInfo(map).name }) : t("plan.emptyAll", { map: mapInfo(map).name })}</h1>
            <p className="muted">{t("plan.emptyHelp")}</p>
            {newLinks}
          </div>
        </div>
      )}
    </div>
  );
}
